import { type PropsWithChildren, type ReactNode } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import { CheckSmallIcon } from '@/icons';
import { useCountryCode } from '@/shared/hooks';
import { formatCurrencyPrice } from '@/shared/utils/currency';
import { Chip } from '@/ui_v3/components/chip/Chip';
import { cn } from '@/utils';
export interface PricingCardProps {
  className?: string;
  ctaButtonText?: string;
  ctaButtonDisabled?: boolean;
  onCtaButtonClick?: () => void;
  /** 카드 하단 슬롯(예: KR "플랜별 기능 비교" 링크). 미전달 시 렌더 안 함. */
  footer?: ReactNode;
}
interface PricingCardContainerProps extends PropsWithChildren {
  className?: string;
}
export const Container = ({
  children,
  className
}: PricingCardContainerProps) => {
  return <article className={cn('overflow-hidden w-full max-w-[640px] rounded-xl border border-alternative bg-white shadow-md', className)} data-sentry-component="Container" data-sentry-source-file="PricingCard.tsx">
      <div className="flex h-full w-full flex-col rounded-[30px] bg-white">
        {children}
      </div>
    </article>;
};
interface PricingCardTopSectionProps extends PropsWithChildren {
  className?: string;
}
export const TopSection = ({
  children,
  className
}: PricingCardTopSectionProps) => {
  return <div className={cn('p-xl pb-lg flex flex-col gap-lg', className)} data-sentry-component="TopSection" data-sentry-source-file="PricingCard.tsx">
      {children}
    </div>;
};
interface PricingCardHeaderProps extends PropsWithChildren {
  className?: string;
}
export const Header = ({
  children,
  className
}: PricingCardHeaderProps) => {
  return <header className={cn('flex items-center gap-2xs', className)} data-sentry-component="Header" data-sentry-source-file="PricingCard.tsx">
      {children}
    </header>;
};
interface PricingCardPaidPlanPriceProps {
  listPrice: number;
  finalPrice: number;
  currency: string;
  className?: string;
  isFirstPurchase?: boolean;
  priceClassName?: string;
  discountRate: number | null;
}
export const PaidPlanPrice = ({
  listPrice,
  finalPrice,
  currency,
  className,
  isFirstPurchase = false,
  priceClassName,
  discountRate
}: PricingCardPaidPlanPriceProps) => {
  const {
    t
  } = useTranslation(['common', 'billing']);
  const {
    isKR
  } = useCountryCode();
  const renderPrice = () => {
    if (isKR) {
      if (isFirstPurchase && !!discountRate) {
        return <div className="flex flex-col gap-4xs">
            <div className="flex items-center gap-2xs">
              <span className="text-[16px] font-semibold leading-[22.4px] text-[#9e9faf] line-through">
                {formatCurrencyPrice(listPrice, currency)}
              </span>
              <span className="inline-flex min-h-[22px] items-center justify-center overflow-hidden rounded-[6px] px-2xs text-[11px] font-semibold leading-[15.4px] text-white" style={{
              background: 'linear-gradient(90deg, #FF737B 0%, #F268F5 100%)'
            }}>
                {`${t('common:discount.firstPurchase')} -${discountRate * 100}%`}
              </span>
            </div>
            <strong className="bg-clip-text text-[40px] font-extrabold leading-[52px] text-transparent" style={{
            backgroundImage: 'linear-gradient(90deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.2) 100%), linear-gradient(90deg, #18d0b4 0%, #3cd84c 100%)'
          }}>
              {formatCurrencyPrice(listPrice * (1 - discountRate), currency)}
            </strong>
          </div>;
      }
      return <strong className={cn('text-display-sm-extrabold', isFirstPurchase ? 'mt-[24px]' : 'mt-0', priceClassName)}>
          {formatCurrencyPrice(finalPrice, currency)}
        </strong>;
    }

    // Other countries (Global) — Early Bird 할인은 listPrice/finalPrice 차이로 계산
    const earlyBirdRate = listPrice > 0 ? Math.round((listPrice - finalPrice) / listPrice * 100) : 0;
    return <div className="flex flex-col gap-4xs" data-sentry-component="renderPrice" data-sentry-source-file="PricingCard.tsx">
        <div className="flex flex-wrap gap-2xs">
          <span className="text-body-lg-semibold text-assistive line-through">
            {formatCurrencyPrice(listPrice, currency)}
          </span>
          <Chip className="border-destructive text-destructive" size="sm" data-sentry-element="Chip" data-sentry-source-file="PricingCard.tsx">
            {t('common:pricing.earlyBird')} -{earlyBirdRate}%
          </Chip>
        </div>
        <strong className="text-display-sm-extrabold">
          {formatCurrencyPrice(finalPrice, currency)}
        </strong>
      </div>;
  };
  return <div className={cn('flex items-end gap-xs flex-wrap min-h-[52px]', className)} data-sentry-component="PaidPlanPrice" data-sentry-source-file="PricingCard.tsx">
      {renderPrice()}
      <span className="flex flex-col pb-3xs text-body-sm-medium text-alternative">
        <Trans t={t} i18nKey="common:pricing.perTeacherPerMonth" components={{
        Br: <br />
      }} data-sentry-element="Trans" data-sentry-source-file="PricingCard.tsx" />
      </span>
    </div>;
};
interface PricingCardBottomSectionProps extends PropsWithChildren {
  className?: string;
}
export const BottomSection = ({
  children,
  className
}: PricingCardBottomSectionProps) => {
  return <div className={cn('p-xl pt-lg flex flex-col gap-md', className)} data-sentry-component="BottomSection" data-sentry-source-file="PricingCard.tsx">
      {children}
    </div>;
};
interface PricingCardDescriptionProps extends PropsWithChildren {
  className?: string;
}
export const Description = ({
  children,
  className
}: PricingCardDescriptionProps) => {
  return <p className={cn('text-body-md-medium text-neutral min-h-[40px]', className)} data-sentry-component="Description" data-sentry-source-file="PricingCard.tsx">
      {children}
    </p>;
};
interface PricingCardDividerProps {
  className?: string;
}
export const Divider = ({
  className
}: PricingCardDividerProps) => {
  return <hr className={cn('h-px border-alternative', className)} data-sentry-component="Divider" data-sentry-source-file="PricingCard.tsx" />;
};
interface PricingCardFeatureTitlesProps {
  mainTitle: string;
  subTitle: string;
  className?: string;
}
export const FeatureTitles = ({
  mainTitle,
  subTitle,
  className
}: PricingCardFeatureTitlesProps) => {
  return <div className={cn('flex flex-col gap-2xs', className)} data-sentry-component="FeatureTitles" data-sentry-source-file="PricingCard.tsx">
      <h4 className="text-body-lg-bold">{mainTitle}</h4>
      <p className="text-body-md-medium text-neutral">{subTitle}</p>
    </div>;
};
interface PricingCardFeatureItemListProps {
  items: string[];
  className?: string;
}
export const FeatureItemList = ({
  items,
  className
}: PricingCardFeatureItemListProps) => {
  return <ul className={cn('flex flex-col gap-xs', className)} data-sentry-component="FeatureItemList" data-sentry-source-file="PricingCard.tsx">
      {items.map(feature => <li key={feature} className="flex items-center gap-2xs">
          <CheckSmallIcon size="md" className="[&>*]:fill-violet-300" />
          <p className="break-words text-body-md-medium">{feature}</p>
        </li>)}
    </ul>;
};
interface PricingCardAiFeatureItemListProps {
  title: string;
  items: string[];
  className?: string;
}
export const AiFeatureItemList = ({
  title,
  items,
  className
}: PricingCardAiFeatureItemListProps) => {
  return <div className={cn('gap-sm rounded-md bg-neutral px-lg py-sm flex flex-col', className)} data-sentry-component="AiFeatureItemList" data-sentry-source-file="PricingCard.tsx">
      <div className="flex items-center gap-2xs">
        <img className="m-[2px] size-[20px]" src="/assets/javis_small.webp" alt="AI features" />
        <h5 className="text-body-lg-bold">{title}</h5>
      </div>
      <FeatureItemList items={items} data-sentry-element="FeatureItemList" data-sentry-source-file="PricingCard.tsx" />
    </div>;
};